import { Component } from '@theme/component'
import {
  hasScrollLockOwner,
  releaseScrollLock,
  scrollLock
} from '@theme/events'
import { removeTrapFocus, trapFocus } from '@theme/focus'
import {
  isClickedOutside,
  isPointWithinElement,
  onAnimationEnd
} from '@theme/maths'
import { mediaQueryMobile } from '@theme/use-device-detection'

export class UiDialog extends Component {
  requiredRefs = ['dialog']

  isAnimating = false

  #isNormalizingSide = false

  #handleSearchDrawerChange = () => {
    this.setAttribute('side', mediaQueryMobile.matches ? 'left' : 'right')
  }

  #handleHashChange = () => {
    this.#openFromHash()
  }

  static get observedAttributes() {
    return ['side']
  }

  #handleDialogCancel = (event) => {
    event.preventDefault()
    void this.closeDialog()
  }

  closeDialog = async () => {
    const { dialog } = this.refs

    if (!dialog?.open || this.isAnimating) {
      return
    }

    this.removeEventListener('click', this.#handleClick)
    this.removeEventListener('keydown', this.#handleKeyDown)
    this.#clearOwnHash()

    this.isAnimating = true
    this.setAttribute('closing', '')
    this._setAriaExpanded(false)
    removeTrapFocus()

    await this.#animateClose(dialog)
  }

  toggleDialog = () => {
    if (this.refs.dialog?.open || this.dataset.nav === 'open') {
      void this.closeDialog()
    } else {
      void this.showDialog()
    }
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'side') {
      this._updateSideClasses(value)
    }
  }

  connectedCallback() {
    this.#prepareOverlay()

    super.connectedCallback()

    this.panel =
      this.querySelector(
        '.drawer-panel, [data-drawer-panel], [role="navigation"], [role="search"], [role="dialog"]'
      ) ?? this.refs.dialog

    const drawerId = this.getAttribute('id') || `drawer-${Date.now()}`
    const overlayId = this.refs.dialog?.id || `${drawerId}-overlay`

    if (!this.hasAttribute('id')) {
      this.setAttribute('id', drawerId)
    }
    if (this.refs.dialog && !this.refs.dialog.id) {
      this.refs.dialog.id = overlayId
    }

    this._overlayId = overlayId

    this.#assignToggleButtons()
    this.#bindDialogEvents()

    this.dataset.nav = this.dataset.nav || 'closed'
    this.inert = this.dataset.nav !== 'open'
    if (!this.hasAttribute('side')) {
      const initialSide = this.dataset.side
      this.setAttribute('side', initialSide || 'left')
    }
    this._updateSideClasses()
    this._setAriaExpanded(this.dataset.nav === 'open')

    if (this.dataset.drawer === 'search') {
      mediaQueryMobile.addEventListener(
        'change',
        this.#handleSearchDrawerChange
      )
      this.#handleSearchDrawerChange()
    }

    window.addEventListener('hashchange', this.#handleHashChange)
    this.#openFromHash()
  }

  disconnectedCallback() {
    if (this.allToggleBtns?.length) {
      for (const btn of this.allToggleBtns) {
        btn.removeEventListener('click', this.toggleHandler)
      }
    }

    this.#unbindDialogEvents()

    if (this.dataset.drawer === 'search') {
      mediaQueryMobile.removeEventListener(
        'change',
        this.#handleSearchDrawerChange
      )
    }

    window.removeEventListener('hashchange', this.#handleHashChange)

    super.disconnectedCallback()
  }

  #openFromHash() {
    if (window.location.hash.slice(1) !== this.getAttribute('id')) {
      return
    }

    void this.showDialog()
  }

  #clearOwnHash() {
    if (window.location.hash.slice(1) !== this.getAttribute('id')) {
      return
    }

    const { pathname, search } = window.location
    window.history.replaceState(null, '', `${pathname}${search}`)
  }

  async showDialog() {
    const { dialog } = this.refs

    if (!dialog || dialog.open || this.isAnimating) {
      return
    }

    this.isAnimating = true
    this.removeAttribute('closing')
    this.inert = false
    this._setAriaExpanded(true)
    this.dataset.nav = 'open'
    this._cancelAnimations()

    dialog.showModal()
    if (this.lockScroll) {
      scrollLock(true)
    }
    this.dispatchEvent(new Event('dialog:open', { bubbles: true }))

    queueMicrotask(() => {
      this.addEventListener('click', this.#handleClick)
      this.addEventListener('keydown', this.#handleKeyDown)
    })

    await this.#animateOpen(dialog)
  }

  #bindDialogEvents() {
    if (!this.refs.dialog) {
      return
    }
    this.refs.dialog.removeEventListener('cancel', this.#handleDialogCancel)
    this.refs.dialog.addEventListener('cancel', this.#handleDialogCancel)
  }

  #unbindDialogEvents() {
    if (!this.refs.dialog) {
      return
    }
    this.refs.dialog.removeEventListener('cancel', this.#handleDialogCancel)
  }

  toggle() {
    this.toggleDialog()
  }

  open() {
    void this.showDialog()
  }

  close() {
    void this.closeDialog()
  }

  #handleClick(event) {
    const { dialog } = this.refs

    if (!dialog) {
      return
    }

    const clickedOutsideDialog = isClickedOutside(event, dialog)
    const clickedOverlay =
      !clickedOutsideDialog &&
      dialog.hasAttribute('data-overlay') &&
      this.panel &&
      !isPointWithinElement(event.clientX, event.clientY, this.panel)

    if (clickedOutsideDialog || clickedOverlay) {
      void this.closeDialog()
    }
  }

  #handleKeyDown(event) {
    if (event.key !== 'Escape') {
      return
    }

    event.preventDefault()
    void this.closeDialog()
  }

  get lockScroll() {
    return this.getAttribute('lock-scroll') !== 'false'
  }

  #animateOpen(dialog) {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        this.panel?.getBoundingClientRect()
        this.setAttribute('open', '')

        const target = this.panel ?? dialog

        if (!target) {
          this.isAnimating = false
          resolve(null)
          return
        }

        onAnimationEnd(target, undefined, { subtree: false }).finally(() => {
          this.isAnimating = false
          if (this.panel) {
            trapFocus(this.panel)
          }
          resolve(null)
        })
      })
    })
  }

  #animateClose(dialog) {
    dialog.classList.add('dialog-closing')

    const target = this.panel ?? dialog

    return new Promise((resolve) => {
      const finalize = () => {
        this.removeAttribute('open')
        this.removeAttribute('closing')
        this.dataset.nav = 'closed'
        this.inert = true
        this.isAnimating = false

        dialog.close()
        dialog.classList.remove('dialog-closing')

        if (this.lockScroll) {
          releaseScrollLock()
        }

        this.dispatchEvent(new Event('dialog:close', { bubbles: true }))
        this.dispatchEvent(new Event('drawer:close', { bubbles: true }))

        resolve(null)
      }

      if (!target) {
        finalize()
        return
      }

      onAnimationEnd(target, undefined, { subtree: false }).finally(finalize)
    })
  }

  #assignToggleButtons() {
    const drawerId = this.getAttribute('id') || ''
    const overlayId = this._overlayId || this.refs.dialog?.id || ''

    const internalToggleBtns = Array.from(
      this.querySelectorAll('button, [role="button"], [data-toggle]')
    ).filter(
      (btn) => !btn.closest('.drawer-dialog, [data-overlay], .drawer-overlay')
    )
    const externalToggleBtns = overlayId
      ? Array.from(
          document.querySelectorAll(
            `button[aria-controls="${overlayId}"], [data-toggle="${drawerId}"], [data-drawer-toggle="${drawerId}"]`
          )
        ).filter((btn) => !this.contains(btn))
      : []

    this.allToggleBtns = internalToggleBtns.concat(externalToggleBtns)

    this.toggleHandler = (ev) => {
      ev.preventDefault()
      this.toggle()
    }

    for (const btn of this.allToggleBtns) {
      btn.removeEventListener('click', this.toggleHandler)
      btn.addEventListener('click', this.toggleHandler)
    }

    this._setAriaExpanded(this.dataset.nav === 'open')
  }

  #prepareOverlay() {
    this.panel = undefined
    this.overlay =
      this.querySelector('.drawer-dialog') ?? this.refs.dialog ?? undefined

    if (this.overlay && !this.overlay.hasAttribute('ref')) {
      this.overlay.setAttribute('ref', 'dialog')
    }
  }

  _setAriaExpanded(isOpen) {
    if (!this.allToggleBtns?.length) {
      return
    }

    const expanded = isOpen ? 'true' : 'false'
    for (const btn of this.allToggleBtns ?? []) {
      try {
        btn.setAttribute('aria-expanded', expanded)
      } catch {}
    }
  }

  _updateSideClasses(value) {
    const raw = value ?? this.getAttribute('side') ?? this.dataset.side
    const normalized = raw === 'right' ? 'right' : 'left'

    if (raw !== normalized) {
      if (this.#isNormalizingSide) {
        return
      }

      this.#isNormalizingSide = true
      this.setAttribute('side', normalized)
      this.#isNormalizingSide = false
      return
    }

    this._applySideState(normalized)
  }

  _applySideState(side) {
    this.dataset.side = side
    if (this.panel) {
      this.panel.dataset.side = side
    }
    if (this.overlay) {
      this.overlay.dataset.side = side
      this.overlay.setAttribute('side', side)
    }
  }

  _cancelAnimations() {
    if (!this.panel?.getAnimations) {
      return
    }

    let animations
    try {
      animations = this.panel.getAnimations()
    } catch {
      return
    }

    for (const animation of animations) {
      try {
        animation.cancel()
      } catch {}
    }
  }
}

if (!customElements.get('dialog-element')) {
  customElements.define('dialog-element', UiDialog)
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement) {
      scrollLock(hasScrollLockOwner())
    }
  },
  { capture: true }
)
